/**
 * Marketing Button — primary (cyan glow), secondary (glass), ghost, outline.
 *
 * @spec /doc/specs/frontend/design-system/design-tokens-v2.md
 * @userStory US-EP022-010
 */

import { forwardRef } from 'react';
import type { ComponentPropsWithRef, ElementType, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-cyan text-white shadow-glow-cyan-md hover:shadow-glow-cyan-lg hover:-translate-y-0.5 active:translate-y-0',
  secondary: 'glass-card text-white hover:bg-white/8 border-white/10 hover:border-white/20',
  ghost: 'text-white/80 hover:text-white hover:bg-white/5',
  outline:
    'bg-transparent text-cyan-300 border border-cyan-500/40 hover:border-cyan-500 hover:bg-cyan-500/10',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-9 px-4 text-sm rounded-lg',
  md: 'h-11 px-5 text-sm rounded-xl',
  lg: 'h-13 px-7 text-base rounded-xl',
};

type CommonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  className?: string;
  children?: ReactNode;
};

type PolymorphicProps<E extends ElementType> = CommonProps &
  Omit<ComponentPropsWithRef<E>, keyof CommonProps | 'as'> & {
    as?: E;
  };

const baseClasses =
  'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950 disabled:opacity-50 disabled:cursor-not-allowed';

function ButtonInner<E extends ElementType = 'button'>(
  {
    as,
    variant = 'primary',
    size = 'md',
    loading = false,
    iconLeft,
    iconRight,
    className,
    children,
    ...rest
  }: PolymorphicProps<E>,
  ref: React.Ref<Element>
) {
  const Component = (as ?? 'button') as ElementType;
  const isDisabled = loading || (rest as { disabled?: boolean }).disabled;

  return (
    <Component
      ref={ref}
      className={cn(baseClasses, VARIANT_CLASSES[variant], SIZE_CLASSES[size], className)}
      aria-busy={loading || undefined}
      {...(Component === 'button' ? { type: (rest as { type?: string }).type ?? 'button' } : {})}
      {...(isDisabled && Component === 'button' ? { disabled: true } : {})}
      {...rest}
    >
      {loading ? (
        <Loader2 size={16} className="animate-spin" aria-hidden="true" />
      ) : (
        iconLeft && (
          <span className="inline-flex shrink-0" aria-hidden="true">
            {iconLeft}
          </span>
        )
      )}
      {children && <span>{children}</span>}
      {!loading && iconRight && (
        <span className="inline-flex shrink-0" aria-hidden="true">
          {iconRight}
        </span>
      )}
    </Component>
  );
}

export const Button = forwardRef(ButtonInner) as <E extends ElementType = 'button'>(
  props: PolymorphicProps<E> & { ref?: React.Ref<Element> }
) => React.ReactElement;
